import * as React from "react";
import { Button, Col, Form, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { FormValues } from "components/form/contracts";
import { RequiredLabel, TextInput, RadioButtons } from "components/form/controls";
import { handleFormInputChange } from "components/form/event-handlers";
import { ContactInformation } from "models/contact";
import { haveErrors, validateFormValues } from "components/form/validators";

type Props = {
  onClose: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onSubmit: (contactInfo: ContactInformation) => void;
};

type FormKeys = "name" | "contactmethod" | "email" | "phone";

const initialState = {
  name: {
    value: "", label: "Name", placeholder: "Enter your name",
    isValid: false, touched: false,
    validationRules: { isRequired: true }, validationError: "",
  },
  contactmethod: {
    value: "", label: "Preferred Method of Contact", placeholder: "",
    isValid: false, touched: false,
    validationRules: { isRequired: true }, validationError: "",
    options: [
      { label: "Email", value: "email" },
      { label: "Phone", value: "phone" },
    ],
  },
  email: {
    value: "", label: "Email", placeholder: "Your email",
    isValid: false, touched: false,
    validationRules: { isRequired: true, isEmail: true }, validationError: "",
  },
  phone: {
    value: "", label: "Telephone", placeholder: "xxx-xxx-xxxx",
    isValid: false, touched: false,
    validationRules: { isPhone: true }, validationError: "",
  },
};

export function GetContactInformationModal(props: Props) {
  const [form, setForm] = React.useState<FormValues<FormKeys>>(initialState);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(handleFormInputChange<HTMLInputElement, FormKeys>(e, form));
  };

  const handleConfirmEstimateSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    validateFormValues(form);
    if (haveErrors(form)) {
      setForm({ ...form });
      return;
    }
    props.onSubmit({
      name: form.name.value,
      email: form.email.value,
      phone: form.phone.value,
      preferredMethodOfContact: form.contactmethod.value,
    });
  };

  const closeBtn = (
    <button
      className="btn-close btn-close-white ms-auto"
      onClick={props.onClose}
      aria-label="Close"
    />
  );

  return (
    <Modal isOpen={true} modalClassName="modal-fullscreen-sm-down">
      <ModalHeader className="bg-primary text-white" close={closeBtn}>
        Confirm Your Estimate
      </ModalHeader>
      <ModalBody>
        <p className="text-muted small mb-3">
          Provide the best way to reach you — we'll schedule a free onsite visit at no extra charge.
        </p>
        <Form>
          <Row>
            <Col md={12}>
              <RequiredLabel>{form.name.label}</RequiredLabel>
              <TextInput type="text" name="name" formControl={form.name} onChange={handleFormChange} />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <RequiredLabel>{form.contactmethod.label}</RequiredLabel>
              <RadioButtons name="contactmethod" onChange={handleFormChange} formControl={form.contactmethod} />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <RequiredLabel>{form.email.label}</RequiredLabel>
              <TextInput type="email" name="email" formControl={form.email} onChange={handleFormChange} />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <RequiredLabel>{form.phone.label}</RequiredLabel>
              <TextInput type="text" name="phone" formControl={form.phone} onChange={handleFormChange} />
            </Col>
          </Row>
        </Form>
      </ModalBody>
      <ModalFooter className="bg-light justify-content-end">
        <Button color="success" onClick={handleConfirmEstimateSubmit}>
          Confirm →
        </Button>
        <Button outline color="secondary" onClick={props.onClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}
